import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import type { Child, Filing, StateRequirement } from '../lib/types';
import { Sheet } from '../ui/Sheet';
import { ComplianceCard, CustomTarget, StateChoice } from './Compliance';
import { Deadlines } from './Deadlines';
import { useParent } from './ParentContext';

/**
 * State compliance, its own section on a child's page (#954). It's off until the family's owner turns it on and agrees
 * to what it keeps (each child's legal name and date of birth, and their own legal name, home address and phone).
 * Then: Progress (days and hours against the state's rule), Deadlines & documents, and Where you file. Turning it off
 * deletes those details, and the made documents if asked.
 */

interface ComplianceData {
  enabled: boolean;
  on: boolean;
  selectedId: string;
  consentAt: number;
  consentText: string;
  canChange: boolean;
  madeDocuments: number;
  filing: Filing;
  customDays: number;
  customHours: number;
  requirements: StateRequirement[];
}

export type CompliancePage = 'compliance' | 'deadlines' | 'filing';

export function StateCompliancePage({ child, page }: { child: Child; page: CompliancePage }) {
  const [data, setData] = useState<ComplianceData | null>(null);
  const load = useCallback(() => {
    api
      .get<ComplianceData>('/api/compliance')
      .then(setData)
      .catch(() => setData(null));
  }, []);
  useEffect(load, [load]);
  if (!data) return null;
  if (!child.stateCompliance?.on) return <ComplianceOff child={child} data={data} onChange={load} />;
  if (page === 'deadlines')
    return (
      <section className="card stack">
        <h2>Deadlines &amp; documents</h2>
        <p className="muted small">Your state’s filing deadlines this school year. Make each document from {child.name}’s records, then mark it filed once it’s sent.</p>
        <Deadlines child={child} />
      </section>
    );
  if (page === 'filing') return <WhereYouFile data={data} onChange={load} />;
  return (
    <>
      <ComplianceCard child={child} />
      <PaperworkDetails child={child} data={data} onChange={load} />
    </>
  );
}

/** Off: what it does, what turning it on keeps, and (for the owner) the opt-in. */
function ComplianceOff({ child, data, onChange }: { child: Child; data: ComplianceData; onChange: () => void }) {
  const { refresh, toast } = useParent();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(data.selectedId);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const before = data.requirements.find((r) => r.id === data.selectedId);

  async function turnOn() {
    setBusy(true);
    try {
      await api.put('/api/compliance', { requirementId: pick, agree: true });
      await refresh();
      onChange();
      setOpen(false);
      toast({ text: `State compliance is on. Add each child’s legal name, date of birth and grade on their Profile.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack compliance-off">
      <h2>State compliance is off</h2>
      <p>
        Track {child.name}’s days and hours against your state’s requirement, get reminders before each filing deadline, and have notices of intent, IHIPs and
        quarterly reports filled in for you.
      </p>
      {before && (
        <p className="muted small">
          You were tracking {before.stateName} · {before.option}. State compliance now asks before it keeps any personal details: agree to turn it back on.
        </p>
      )}
      <div className="compliance-keeps stack">
        <span className="field-label">Turning it on means we’ll also keep</span>
        <span>Each child’s full legal name and date of birth, and your legal name, home address and phone.</span>
        <span className="muted small">Nothing about them is kept while it’s off.</span>
      </div>
      {data.canChange ? (
        <div>
          <button className="btn primary" onClick={() => setOpen(true)}>
            Set up state compliance
          </button>
        </div>
      ) : (
        <p className="muted small">Only your family’s owner can turn it on.</p>
      )}
      {open && (
        <Sheet title="Turn on state compliance" onClose={() => setOpen(false)}>
          <div className="stack">
            <StateChoice requirements={data.requirements} selectedId={pick} onPick={setPick} />
            <div className="compliance-keeps stack">
              <strong>What we’ll collect, and why</strong>
              <span>
                • <strong>Each child’s full legal name and date of birth</strong>, on their Profile. Their grade becomes required too.
              </span>
              <span>
                • <strong>Your legal name, home address and phone</strong>, on your account.
              </span>
              <span className="muted">
                They’re used only to fill in the state documents you make, are seen only by you and the adults you share each child with, and are never sold or
                shared. Turn state compliance off at any time to delete them.
              </span>
              <Link to="/privacy" target="_blank" className="small">
                Read the Privacy Policy
              </Link>
            </div>
            <label className="check">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>{data.consentText}</span>
            </label>
            <div className="sheet-actions">
              <button className="btn" onClick={() => setOpen(false)}>
                Not now
              </button>
              <button className="btn primary" disabled={!agreed || !pick || busy} onClick={turnOn}>
                Turn on
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </section>
  );
}

/** Where the details live, what's missing for this child, and (for the owner) turning it off. */
function PaperworkDetails({ child, data, onChange }: { child: Child; data: ComplianceData; onChange: () => void }) {
  const { refresh, toast } = useParent();
  const [open, setOpen] = useState(false);
  const [alsoDocs, setAlsoDocs] = useState(true);
  const [busy, setBusy] = useState(false);
  const s = child.stateCompliance!;
  const missing = [!s.legalName && 'full legal name', !s.birthDate && 'date of birth', !(child.grade ?? '').trim() && 'grade'].filter(Boolean) as string[];

  async function turnOff() {
    setBusy(true);
    try {
      const r = await api.del<{ deletedDocuments: number }>(`/api/compliance${alsoDocs && data.madeDocuments ? '?deleteDocuments=1' : ''}`);
      await refresh();
      onChange();
      setOpen(false);
      toast({
        text: `State compliance is off. Legal names, birth dates and your address were deleted${r.deletedDocuments ? `, with ${r.deletedDocuments} made document${r.deletedDocuments === 1 ? '' : 's'}` : ''}.`,
        tone: 'good',
      });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card stack">
      <div className="row-between wrap">
        <div className="stack tight">
          <strong>Details for state paperwork</strong>
          <span className="small">
            {child.name}’s legal name, date of birth and grade are on <Link to={`/kids/${child.id}/profile`}>their Profile</Link>. Your legal name and home address are
            in <Link to="/settings#account">Settings → Your profile</Link>.
          </span>
          {missing.length > 0 && (
            <span className="warn-text small">
              Still needed for {child.name}: {missing.join(', ')}.
            </span>
          )}
        </div>
        {data.canChange && (
          <button className="btn" onClick={() => setOpen(true)}>
            Turn off state compliance
          </button>
        )}
      </div>
      {open && (
        <Sheet title="Turn off state compliance?" onClose={() => setOpen(false)}>
          <div className="stack">
            <p>
              This deletes every child’s legal name and date of birth and your legal name, home address and phone, and stops deadline reminders. Attendance hours,
              grades and the rest of your records stay.
            </p>
            {data.madeDocuments > 0 && (
              <label className="check">
                <input type="checkbox" checked={alsoDocs} onChange={(e) => setAlsoDocs(e.target.checked)} />
                <span>
                  Also delete the {data.madeDocuments} notice{data.madeDocuments === 1 ? '' : 's'}, plans and reports ClassGuardian made and kept in Homeschool documents
                  (they show legal names and birth dates).
                </span>
              </label>
            )}
            <div className="sheet-actions">
              <button className="btn" onClick={() => setOpen(false)}>
                Keep it on
              </button>
              <button className="btn primary" disabled={busy} onClick={turnOff}>
                Turn off and delete
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </section>
  );
}

/** Where you file: the state and option, a target the umbrella school sets, and the official and district. */
function WhereYouFile({ data, onChange }: { data: ComplianceData; onChange: () => void }) {
  const { refresh, toast } = useParent();
  const [filing, setFiling] = useState<Filing>(data.filing);
  const [status, setStatus] = useState<'' | 'saving' | 'saved'>('');
  const selected = data.requirements.find((r) => r.id === data.selectedId);
  const set = (patch: Partial<Filing>) => setFiling({ ...filing, ...patch });
  // Saved as you type (#961): a moment after the last change, and right away when a field loses focus or the page closes.
  const savedRef = useRef(JSON.stringify(data.filing));
  const latest = useRef(filing);
  latest.current = filing;
  const saveFiling = useCallback(async () => {
    const next = latest.current;
    const key = JSON.stringify(next);
    if (key === savedRef.current) return;
    savedRef.current = key;
    setStatus('saving');
    try {
      await api.put('/api/compliance/filing', next);
      setStatus('saved');
    } catch (e) {
      savedRef.current = '';
      setStatus('');
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }, [toast]);
  useEffect(() => {
    const t = setTimeout(() => void saveFiling(), 800);
    return () => clearTimeout(t);
  }, [filing, saveFiling]);
  useEffect(() => () => void saveFiling(), [saveFiling]);

  async function put(body: Record<string, unknown>, text: string) {
    try {
      await api.put('/api/compliance', { requirementId: data.selectedId, ...body });
      await refresh();
      onChange();
      toast({ text, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <>
      <section className="card stack">
        <h2>Your state</h2>
        {data.canChange ? (
          <StateChoice requirements={data.requirements} selectedId={data.selectedId} onPick={(id) => id !== data.selectedId && put({ requirementId: id }, 'Your state’s requirement is changed.')} />
        ) : (
          <p>{selected ? `${selected.stateName} · ${selected.option}` : 'Not set'}</p>
        )}
        {selected && !selected.days && !selected.hours && data.canChange && (
          <CustomTarget
            days={data.customDays}
            hours={data.customHours}
            onSave={(customDays, customHours) => put({ customDays, customHours }, customDays || customHours ? 'Saved. Progress tracks it.' : 'Cleared.')}
          />
        )}
      </section>
      <section className="card stack">
        <div className="row-between wrap">
          <h2>Where you file</h2>
          <span className="muted small" aria-live="polite">
            {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : ''}
          </span>
        </div>
        <p className="muted small">The official your notices and reports go to. These are printed in the “To” part of each document. Changes save as you type.</p>
        <div className="field-row" onBlur={() => void saveFiling()}>
          <label className="grow">
            Official’s name
            <input value={filing.officialName} onChange={(e) => set({ officialName: e.target.value })} maxLength={120} placeholder="e.g. Dr. Maria Chen" />
          </label>
          <label className="grow">
            Title
            <input value={filing.officialTitle} onChange={(e) => set({ officialTitle: e.target.value })} maxLength={80} />
          </label>
        </div>
        <div className="field-row" onBlur={() => void saveFiling()}>
          <label className="grow">
            School district
            <input value={filing.district} onChange={(e) => set({ district: e.target.value })} maxLength={160} />
          </label>
          <label className="grow">
            District address <span className="muted small">(optional)</span>
            <textarea rows={2} value={filing.districtAddress} onChange={(e) => set({ districtAddress: e.target.value })} maxLength={300} />
          </label>
        </div>
      </section>
    </>
  );
}
